import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ensureGooglePlacesLoaded } from '../utils/googlePlacesLoader';

type Props = {
  address: string | null | undefined;
  height?: number;
};

/** Small embedded Google Map with a single pin, geocoded from a plain address string. */
const AddressMiniMap: React.FC<Props> = ({ address, height = 160 }) => {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    const addr = (address || '').trim();
    if (!addr) {
      setStatus('error');
      return;
    }
    setStatus('loading');

    ensureGooglePlacesLoaded().then((ok) => {
      if (cancelled) return;
      const g = (window as any).google;
      if (!ok || !g?.maps || !mapDivRef.current) {
        setStatus('error');
        return;
      }
      const geocoder = new g.maps.Geocoder();
      geocoder.geocode({ address: addr }, (results: any[], geoStatus: string) => {
        if (cancelled) return;
        if (geoStatus !== 'OK' || !results?.[0]) {
          setStatus('error');
          return;
        }
        const pos = results[0].geometry.location;
        if (!mapRef.current) {
          mapRef.current = new g.maps.Map(mapDivRef.current, {
            center: pos,
            zoom: 15,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            zoomControl: false,
            gestureHandling: 'cooperative',
          });
        } else {
          mapRef.current.setCenter(pos);
        }
        if (markerRef.current) markerRef.current.setMap(null);
        markerRef.current = new g.maps.Marker({ position: pos, map: mapRef.current });
        setStatus('ready');
      });
    });

    return () => {
      cancelled = true;
    };
  }, [address]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height, borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.100' }}>
      <Box ref={mapDivRef} sx={{ width: '100%', height: '100%' }} />
      {status !== 'ready' && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
          }}
        >
          {status === 'loading' ? (
            <CircularProgress size={20} />
          ) : (
            <Typography variant="caption" color="text.secondary">Map unavailable</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AddressMiniMap;
